import { LocalDate } from "@js-joda/core";
import { CustomIcon } from "components/icon/customIcon.component";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { ColorV2 } from "config/colors";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseIsoDateToLocalDateOrNull } from "web-apps/company/utils/timeAndDate";
import { sanitizeNonNegativeDecimalInput } from "utils/input.normalize.logic";
import type { EmploymentDetailsForm } from "../../profilePanel.contract";
import { canApplyMonthlyEffectiveDate } from "../profilePanel.effectiveDate.logic";
import { sortHistoryItemsByStartDateDesc } from "../profilePanel.history.logic";
import {
  SalaryModalAddAction,
  SalaryModalDeleteButton,
  SalaryModalError,
  SalaryModalFormGrid,
  SalaryModalHistoryCell,
  SalaryModalHistoryHeaderCell,
  SalaryModalHistoryHeaderRow,
  SalaryModalHistoryRow,
  SalaryModalHistorySection,
  SalaryModalHistoryTable,
  SalaryModalHistoryTitle,
  SalaryModalInlineInputWrap,
  SalaryModalRowActionButton,
  SalaryModalRowActionGroup,
} from "./profilePanel.salaryModal.styled";
import {
  findSalaryHistoryItemById,
  isEmploymentStartSalaryRow,
  SalaryHistoryItem,
  toEditingSalaryValue,
  toHistorySalary,
  toParsedEditedSalaryValue,
} from "./profilePanel.salaryModal.logic";

interface ProfilePanelSalaryModalProps {
  isOpen: boolean;
  isSaving: boolean;
  saveError: string;
  editedEmploymentDetails: EmploymentDetailsForm;
  salaryHistory: SalaryHistoryItem[];
  salaryType: string | null | undefined;
  effectiveFromDate: LocalDate | null;
  earliestSelectableDate: LocalDate | null;
  onClose: () => void;
  onConfirm: () => void;
  onAddSalary: () => void;
  onDeleteSalary: (salaryId: number) => void;
  onUpdateSalaryValue: (salaryId: number, value: number) => void;
  onChangeFromDate: (date: LocalDate | null) => void;
  onEmploymentFieldChange: <TField extends keyof EmploymentDetailsForm>(
    field: TField,
    value: EmploymentDetailsForm[TField],
  ) => void;
}

/**
 * Handles profile panel salary modal.
 */
export const ProfilePanelSalaryModal = (
  props: ProfilePanelSalaryModalProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [editingSalaryId, setEditingSalaryId] = useState<number | null>(null);
  const [editingSalaryValue, setEditingSalaryValue] = useState<string>("");

  const sortedHistory = useMemo(() => {
    return sortHistoryItemsByStartDateDesc(props.salaryHistory);
  }, [props.salaryHistory]);

  const canAddSalary = canApplyMonthlyEffectiveDate(
    props.effectiveFromDate,
    props.earliestSelectableDate,
  );

  useEffect(() => {
    if (!props.isOpen) {
      setEditingSalaryId(null);
      setEditingSalaryValue("");
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (editingSalaryId == null) return;
    const editedSalary = findSalaryHistoryItemById({
      salaryHistory: props.salaryHistory,
      salaryId: editingSalaryId,
    });
    if (editedSalary == null) {
      setEditingSalaryId(null);
      setEditingSalaryValue("");
    }
  }, [editingSalaryId, props.salaryHistory]);

  const handleStartEdit = (salaryItem: SalaryHistoryItem): void => {
    setEditingSalaryId(salaryItem.id);
    setEditingSalaryValue(toEditingSalaryValue(salaryItem.value));
  };

  const handleCancelEdit = (): void => {
    setEditingSalaryId(null);
    setEditingSalaryValue("");
  };

  const handleSaveEditedSalary = (): void => {
    if (editingSalaryId == null) return;
    props.onUpdateSalaryValue(
      editingSalaryId,
      toParsedEditedSalaryValue(editingSalaryValue),
    );
    setEditingSalaryId(null);
    setEditingSalaryValue("");
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      label={t("Staff.ProfilePanelChangeSalary")}
      size="mediumSlim"
      footer={
        <>
          <SecondaryButton
            label={t("General.Cancel")}
            disabled={props.isSaving}
            onClick={props.onClose}
          />
          <PrimaryButton
            label={t("General.Save")}
            isLoading={props.isSaving}
            disabled={props.isSaving}
            onClick={props.onConfirm}
          />
        </>
      }
    >
      <SalaryModalFormGrid>
        <InputField
          fullWidth
          label={t("Staff.ProfilePanelSalaryNewSalary")}
          value={props.editedEmploymentDetails.salary}
          onChange={(salary) =>
            props.onEmploymentFieldChange(
              "salary",
              sanitizeNonNegativeDecimalInput(salary),
            )
          }
        />
        <DatePicker
          label={t("Staff.ProfilePanelSalaryStartDay")}
          value={props.effectiveFromDate}
          onChange={props.onChangeFromDate}
          monthlySelectableDay={1}
          earliestSelectableDate={props.earliestSelectableDate ?? undefined}
        />
        <SalaryModalAddAction>
          <PrimaryButton
            size="small"
            label={t("General.Add")}
            isLoading={props.isSaving}
            disabled={!canAddSalary || props.isSaving}
            onClick={props.onAddSalary}
          />
        </SalaryModalAddAction>
      </SalaryModalFormGrid>

      <SalaryModalHistorySection>
        <SalaryModalHistoryTitle>
          {t("General.History")}
        </SalaryModalHistoryTitle>
        <SalaryModalHistoryTable>
          <SalaryModalHistoryHeaderRow $columns={4}>
            <SalaryModalHistoryHeaderCell>
              {t("Staff.AddStaffPanelFieldStartDate")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isRightAligned>
              {t("Staff.AddStaffPanelFieldSalaryAmount")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell>
              {t("Staff.AddStaffPanelFieldSalaryModel")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isCentered>
              {""}
            </SalaryModalHistoryHeaderCell>
          </SalaryModalHistoryHeaderRow>
          {sortedHistory.length === 0 ? (
            <SalaryModalHistoryRow $columns={4}>
              <SalaryModalHistoryCell>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isRightAligned>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isCentered>-</SalaryModalHistoryCell>
            </SalaryModalHistoryRow>
          ) : (
            sortedHistory.map((salaryItem) => (
              <SalaryModalHistoryRow key={salaryItem.id} $columns={4}>
                <SalaryModalHistoryCell>
                  {parseIsoDateToLocalDateOrNull(
                    salaryItem.startDate,
                  )?.toString() ?? "-"}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isRightAligned>
                  {editingSalaryId === salaryItem.id ? (
                    <SalaryModalInlineInputWrap>
                      <InputField
                        fullWidth
                        ariaLabel={t("Staff.ProfilePanelSalaryNewSalary")}
                        value={editingSalaryValue}
                        size="compact"
                        onChange={(value) =>
                          setEditingSalaryValue(
                            sanitizeNonNegativeDecimalInput(value),
                          )
                        }
                      />
                    </SalaryModalInlineInputWrap>
                  ) : (
                    toHistorySalary(salaryItem.value)
                  )}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell>
                  {props.salaryType
                    ? t(`Staff.salaryType${props.salaryType}`)
                    : "-"}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isCentered>
                  {(() => {
                    const isEmploymentStartSalary = isEmploymentStartSalaryRow({
                      employmentStartDateIso:
                        props.earliestSelectableDate?.toString() ?? null,
                      salaryStartDate: salaryItem.startDate,
                    });
                    const isEditingThisSalary =
                      editingSalaryId === salaryItem.id;

                    if (isEmploymentStartSalary) {
                      return isEditingThisSalary ? (
                        <SalaryModalRowActionGroup>
                          <SalaryModalRowActionButton
                            type="button"
                            aria-label={t("General.Save")}
                            disabled={props.isSaving}
                            onClick={handleSaveEditedSalary}
                          >
                            <CustomIcon
                              name="v2/checkmark"
                              size="24px"
                              color={ColorV2.Blue1}
                            />
                          </SalaryModalRowActionButton>
                          <SalaryModalRowActionButton
                            type="button"
                            aria-label={t("General.Cancel")}
                            disabled={props.isSaving}
                            onClick={handleCancelEdit}
                          >
                            <CustomIcon
                              name="v2/cross"
                              size="24px"
                              color={ColorV2.Blue1}
                            />
                          </SalaryModalRowActionButton>
                        </SalaryModalRowActionGroup>
                      ) : (
                        <SalaryModalRowActionButton
                          type="button"
                          aria-label={t("General.Edit")}
                          disabled={props.isSaving}
                          onClick={() => handleStartEdit(salaryItem)}
                        >
                          <CustomIcon
                            name="v2/pen"
                            size="20px"
                            color={ColorV2.Blue1}
                          />
                        </SalaryModalRowActionButton>
                      );
                    }

                    return (
                      <SalaryModalDeleteButton
                        type="button"
                        aria-label={t("General.Remove")}
                        disabled={props.isSaving}
                        onClick={() => props.onDeleteSalary(salaryItem.id)}
                      >
                        <CustomIcon name="v2/trash" size="16px" />
                      </SalaryModalDeleteButton>
                    );
                  })()}
                </SalaryModalHistoryCell>
              </SalaryModalHistoryRow>
            ))
          )}
        </SalaryModalHistoryTable>
      </SalaryModalHistorySection>

      {props.saveError.length > 0 && (
        <SalaryModalError>{props.saveError}</SalaryModalError>
      )}
    </Modal>
  );
};
